import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  faceBookCallBack,
  getAllCompanies,
  getAllFBPages,
  createFBPage
} from '../services/api/implementation/impl';
import { useNavigate } from 'react-router-dom';
import Play from '../../shared/assets/svg/play.svg';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const FacebookCompanies = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [OpenViewPages, setOpenViewPages] = useState(false);
  const [code, setCode] = useState(searchParams.get('code'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pages, setPages] = useState([]);
  const { collapsed } = useSidebar();
  const [selectedPage, setSelectedPage] = useState({
    page_id: null,
    name: null,
    pageAccessToken: null,
    company_id: null
  });

  // Load companyId from localStorage on component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Facebook Companies Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const storedCompanyId = localStorage.getItem('companyId');
    if (code) {
      if (storedCompanyId) {
        const body = {
          company_id: parseInt(storedCompanyId),
          code: code
        };
        handleAction({ body });
        setCode(null);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('code');
        setSearchParams(newSearchParams);
      }
    }
  }, [code]);

  const { data: companiesData, refetch } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  const { mutate: handleAction } = useMutation({
    mutationFn: ({ body }) => faceBookCallBack(body),
    onSuccess: async (data) => {
      toast.success(`AccessToken Generated Successfully`);
      setPages(data);
      refetch();
    },
    onError: (err) => {
      console.error(`Error Generating AccessToken`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Generating AccessToken`);
    }
  });

  useEffect(() => {
    if (companiesData) {
      setCompanies(companiesData);
    }
  }, [companiesData]);

  const handleFacebookLogin = (companyId) => {
    const appId = '9566903480068710';
    const redirectUri = `https://app.housingram.com/builder/integration/facebook-companies`;
    const fbLoginUrl = `https://www.facebook.com/v17.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=pages_manage_ads,pages_read_engagement,leads_retrieval,pages_show_list,business_management&response_type=code`;

    // Save companyId to localStorage
    localStorage.setItem('companyId', companyId);

    window.location.href = fbLoginUrl;
  };

  const { mutate: AllPages } = useMutation({
    mutationFn: ({ id }) => getAllFBPages(id),
    onSuccess: async (data, { id }) => {
      setPages(data);
      setSelectedPage((prev) => ({ ...prev, company_id: id }));

      setOpenViewPages(true);
    },
    onError: (err) => {
      console.error(`Error Fetching FB Pages`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Fetching FB Pages`);
    }
  });

  const HandleCreatePage = () => {
    if (!selectedPage.page_id) {
      toast.error('Click On Some Page To Select');
      return;
    }

    handleCreatePage({ selectedPage });
  };

  const { mutate: handleCreatePage } = useMutation({
    mutationFn: ({ selectedPage }) => createFBPage(selectedPage),
    onSuccess: async (data) => {
      toast.success(`Page Connected Successfully`);
      setSelectedPage({
        page_id: null,
        name: null,
        pageAccessToken: null,
        company_id: null
      });
      setOpenViewPages(false);
      refetch();
    },
    onError: (err) => {
      console.error(`Error Connecting Page`, err);

      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes('already associated with another company')
      ) {
        toast.error('Selected Company is already linked with another company');
      } else {
        toast.error(err?.response?.data?.message || err?.message || `Error Connecting Page`);
      }
    }
  });

  return (
    <Layout>
      {/* Header with Back Button */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration')}
          data={{ name: 'Integrations > Facebook' }}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        {/* Main Content */}
        <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
          <div className="grid grid-cols-3 gap-6">
            {companies.map((company) => (
              <div key={company.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col">
                <img className="border h-[6rem]" src={company.company_logo} alt="Company Logo" />
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-bold text-lg">{company.company_name}</h3>
                  <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium ">
                    <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                    <p>Live</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 text-justify">
                  Easily integrate with Facebook to sync data and streamline processes. Whether
                  it&apos;s Ads, Pages, or other tools, our integration ensures smooth connectivity
                  and efficient work without manual transfers.
                </p>
                <div className="w-full flex gap-4 justify-between">
                  <Button displayType="secondary">
                    <span className="flex gap-1 justify-start items-center">
                      <img src={Play} alt="" className="pt-1" />
                      Demo
                    </span>
                  </Button>
                  <Button
                    onClick={() => {
                      if (company.isConnectedToFb) {
                        if (company.page?.is_connected_to_company) {
                          navigate('/builder/integration/facebook-companies/pages', {
                            state: {
                              pageID: company.page?.id,
                              cmpId: company.id
                            }
                          });
                        } else {
                          AllPages({ id: company.id });
                        }
                      } else {
                        handleFacebookLogin(company.id);
                      }
                    }}
                    disabled={isSubmitting}>
                    {company.isConnectedToFb
                      ? company.page?.is_connected_to_company
                        ? 'View Page'
                        : 'Select Page'
                      : ' Configure'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {OpenViewPages && (
        <Modal
          isDirty={true}
          width={60}
          name="FaceBook Pages"
          onClose={() => {
            setOpenViewPages(false);
            setSelectedPage({
              page_id: null,
              name: null,
              pageAccessToken: null,
              company_id: null
            });
          }}>
          <div className="flex flex-col ">
            <div className="grid grid-cols-3 gap-6">
              {pages.map((company) => (
                <div
                  onClick={() =>
                    setSelectedPage((prev) =>
                      prev.page_id === company.id
                        ? { ...prev, page_id: null, name: null, pageAccessToken: null }
                        : {
                            ...prev,
                            page_id: company.id,
                            name: company.name,
                            pageAccessToken: company.access_token
                          }
                    )
                  }
                  key={company.id}
                  className={`bg-white shadow-md p-6 rounded-lg flex flex-col ${
                    selectedPage.page_id === company.id ? 'border-2 border-green-500' : ''
                  }`}>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-lg">{company.name}</h3>
                    <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium ">
                      <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                      <p>Live</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => {
                  setOpenViewPages(false);
                  setSelectedPage({
                    page_id: null,
                    name: null,
                    pageAccessToken: null,
                    company_id: null
                  });
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} onClick={HandleCreatePage}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default FacebookCompanies;
